import { useState, useCallback, useEffect } from 'react';
import { Order } from '../types';

export interface StrategySuggestion {
    id?: string;
    type: 'boomerang' | 'dispatch_hold' | 'production_retention';
    orders: Order[];
    savings: number;
    delayImpact: number;
    rationale: string;
}

export interface StrategyQueueState {
    /** Fila de sugestões aguardando decisão */
    queue: StrategySuggestion[];
    /** Índice da sugestão atual sendo exibida */
    currentIndex: number;
    /** Sugestão sendo mostrada no momento (ou null se fila vazia) */
    current: StrategySuggestion | null;
    /** Total de sugestões na fila */
    total: number;
    /** Posição atual (1-based para exibição) */
    position: number;
    /** Se há mais sugestões após a atual */
    hasNext: boolean;
    /** Se há sugestões anteriores */
    hasPrevious: boolean;
}

interface UseStrategyQueueReturn extends StrategyQueueState {
    /** Inicializa a fila com novas sugestões */
    initializeQueue: (suggestions: StrategySuggestion[]) => void;
    /** Move para a próxima sugestão */
    next: () => void;
    /** Move para a sugestão anterior */
    previous: () => void;
    /** Remove a sugestão atual da fila (após confirmação/rejeição) */
    removeCurrent: () => void;
    /** Limpa toda a fila */
    clearQueue: () => void;
    /** Adiciona sugestões à fila existente */
    appendToQueue: (suggestions: StrategySuggestion[]) => void;
}

/**
 * Hook para gerenciar uma fila sequencial de sugestões de estratégias.
 * 
 * Quando múltiplas estratégias são sugeridas simultaneamente (ex: Bumerangue 
 * para pedido #123 e Despacho Retido para pedido #456), este hook garante
 * que o usuário veja e decida sobre cada uma sequencialmente, mantendo
 * uma UX consistente e evitando sobrecarga cognitiva.
 * 
 * @returns Estado da fila e funções de controle
 * 
 * @example
 * const { current, next, removeCurrent, initializeQueue } = useStrategyQueue();
 * 
 * // Quando SmartSplit retorna sugestões:
 * useEffect(() => {
 *   if (suggestion?.suggestedStrategies?.length > 0) {
 *     initializeQueue(suggestion.suggestedStrategies.map(s => ({
 *       id: generateId(),
 *       ...s
 *     })));
 *   }
 * }, [suggestion]);
 * 
 * // No modal:
 * const handleConfirm = () => {
 *   onConfirm(current.orders, current.type);
 *   removeCurrent(); // Remove atual e avança automaticamente
 * };
 */
export const useStrategyQueue = (): UseStrategyQueueReturn => {
    const [queue, setQueue] = useState<StrategySuggestion[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);

    // Calcula estado derivado
    const current = queue.length > 0 && currentIndex < queue.length 
        ? queue[currentIndex] 
        : null;
    const total = queue.length;
    const position = queue.length > 0 ? currentIndex + 1 : 0;
    const hasNext = currentIndex < queue.length - 1;
    const hasPrevious = currentIndex > 0;

    /** Inicializa a fila com novas sugestões */
    const initializeQueue = useCallback((suggestions: StrategySuggestion[]) => {
        if (suggestions.length === 0) return;
        
        // Gera IDs únicos se não existirem
        const suggestionsWithIds = suggestions.map((s, index) => ({
            ...s,
            id: s.id || `strategy-${Date.now()}-${index}`
        }));
        
        setQueue(suggestionsWithIds);
        setCurrentIndex(0);
    }, []);

    /** Move para a próxima sugestão */
    const next = useCallback(() => {
        setCurrentIndex(prev => Math.min(prev + 1, queue.length - 1));
    }, [queue.length]);

    /** Move para a sugestão anterior */
    const previous = useCallback(() => {
        setCurrentIndex(prev => Math.max(prev - 1, 0));
    }, []);

    /** Remove a sugestão atual da fila */
    const removeCurrent = useCallback(() => {
        setQueue(prev => {
            const newQueue = [...prev];
            newQueue.splice(currentIndex, 1);
            return newQueue;
        });
        
        // Ajusta índice se necessário (se removeu o último, volta um)
        setCurrentIndex(prev => {
            if (prev >= queue.length - 1 && prev > 0) {
                return prev - 1;
            }
            return prev;
        });
    }, [currentIndex, queue.length]);

    /** Limpa toda a fila */
    const clearQueue = useCallback(() => {
        setQueue([]);
        setCurrentIndex(0);
    }, []);

    /** Adiciona sugestões ao final da fila existente */
    const appendToQueue = useCallback((suggestions: StrategySuggestion[]) => {
        const suggestionsWithIds = suggestions.map((s, index) => ({
            ...s,
            id: s.id || `strategy-${Date.now()}-${index}`
        }));
        
        setQueue(prev => [...prev, ...suggestionsWithIds]);
    }, []);

    // Auto-limpa quando fila fica vazia
    useEffect(() => {
        if (queue.length === 0 && currentIndex !== 0) {
            setCurrentIndex(0);
        }
    }, [queue.length, currentIndex]);

    return {
        queue,
        currentIndex,
        current,
        total,
        position,
        hasNext,
        hasPrevious,
        initializeQueue,
        next,
        previous,
        removeCurrent,
        clearQueue,
        appendToQueue
    };
};

export default useStrategyQueue;
